import { z } from 'zod';

/**
 * 利用企業作成フォームのバリデーションスキーマ factory 関数の引数
 */
type CreateClientFormSchemaParams = {
  /**
   * 取引店舗の選択肢が1件以上あるかどうか。
   * true の場合、partnerBranchId を必須項目として扱う（PEN-890）。
   */
  isPartnerBranchRequired: boolean;
};

/**
 * 利用企業作成フォームのバリデーションスキーマ（新版）
 *
 * 取引店舗（partnerBranchId）は選択肢の有無によって必須/任意が切り替わるため、
 * factory 関数として実装している（PEN-890）。
 *
 * @property name - 会社名（必須）
 * @property nameKana - 会社名（フリガナ）（任意）
 * @property representativeName - 代表者氏名（任意）
 * @property corporateNumber - 法人番号（任意）
 * @property externalClientId - 外部企業ID（任意）
 * @property groupId - グループID（任意、単一選択）
 * @property postalCode - 郵便番号（任意、入力する場合はハイフンなし7桁の数字）
 * @property prefectureCode - 都道府県コード（任意）
 * @property city - 市区町村（任意）
 * @property streetAddress - 番地以降（任意）
 * @property employeeCount - 利用予定者数（必須、0以上の整数）
 * @property partnerBranchId - 取引店舗ID（`isPartnerBranchRequired` が true のとき必須、false のとき任意）
 * @property contractPlanId - 契約プランID（必須）
 * @property startedOn - 利用開始日（必須）
 * @property endedOn - 利用終了日（任意、null = 無期限。利用開始日以降の日付のみ許容）
 * @property clientFeatureFlags - クライアント機能フラグ（任意）
 * @property contactPersonName - 担当者氏名（必須）
 * @property contactPersonNameKana - 担当者氏名（フリガナ）（必須）
 * @property contactPersonEmail - メールアドレス（必須）
 * @property contactPersonPhone - 電話番号（必須、数字とハイフンのみ）
 * @property contactPreferredTime - お電話がつながりやすい時間帯（平日）（任意）
 * @property billingContactName - 請求担当者氏名（任意）
 * @property billingContactNameKana - 請求担当者氏名（フリガナ）（任意）
 * @property billingEmail - 請求書送付先メールアドレス（任意、入力する場合はメール形式）
 *
 * @param params - スキーマの挙動を切り替えるパラメーター
 * @param params.isPartnerBranchRequired - 取引店舗の選択肢が1件以上あるかどうか
 */
export const createClientFormSchema = ({ isPartnerBranchRequired }: CreateClientFormSchemaParams) =>
  z
    .object({
      // 会社情報
      name: z.string().trim().min(1, '会社名を入力してください。'),
      nameKana: z.string().trim().optional(),
      representativeName: z.string().trim().optional(),
      corporateNumber: z.string().optional(),
      externalClientId: z.string().optional(),
      groupId: z.string().optional(),
      postalCode: z
        .string()
        .trim()
        .regex(/^\d{7}$/, '郵便番号はハイフンなし7桁の数字で入力してください。')
        .optional()
        .or(z.literal('')),
      prefectureCode: z.string().optional(),
      city: z.string().trim().optional(),
      streetAddress: z.string().trim().optional(),
      employeeCount: z.preprocess(
        val => (typeof val === 'string' && val.trim() === '' ? Number.NaN : val),
        z.coerce
          .number('半角数字で入力してください')
          .int('半角数字で入力してください')
          .gte(0, '0以上の整数を入力してください。')
      ),
      // 取引店舗の選択肢が0件のときは項目自体を非表示にするため任意のまま送信できる必要がある。
      // 選択肢が1件以上あるときのみ、未選択（空文字 / undefined）をエラーとする（PEN-890）。
      partnerBranchId: z
        .string()
        .optional()
        .refine(value => !isPartnerBranchRequired || !!value, {
          message: '取引店舗を選択してください。',
        }),
      // プラン情報
      contractPlanId: z.string().trim().min(1, '契約プランを選択してください。'),
      startedOn: z.date({ message: '利用開始日を入力してください。' }),
      endedOn: z.date({ message: '利用終了日を入力してください。' }).nullable().optional(),
      clientFeatureFlags: z.array(z.string()),
      // 担当者情報
      contactPersonName: z.string().trim().min(1, '担当者氏名を入力してください。'),
      contactPersonNameKana: z.string().trim().min(1, '担当者氏名（フリガナ）を入力してください。'),
      contactPersonEmail: z.email({ message: 'メールアドレスの形式が正しくありません。' }),
      contactPersonPhone: z
        .string()
        .trim()
        .min(1, '電話番号を入力してください。')
        .regex(/^[\d-]+$/, '電話番号は数字とハイフンのみ入力してください。'),
      contactPreferredTime: z.string().trim().optional(),
      // 請求先情報
      billingContactName: z.string().trim().optional(),
      billingContactNameKana: z.string().trim().optional(),
      billingEmail: z
        .email({ message: '請求書送付先メールアドレスの形式が正しくありません。' })
        .optional()
        .or(z.literal('')),
    })
    .refine(data => data.endedOn == null || data.endedOn >= data.startedOn, {
      message: '利用終了日は利用開始日以降の日付を入力してください。',
      path: ['endedOn'],
    });

/**
 * 利用企業作成フォームのフィールド値の型
 */
export type CreateClientFormFieldValues = z.infer<ReturnType<typeof createClientFormSchema>>;
